import React, { useMemo } from 'react';
import { Common, TaskHelpers } from 'ontraccr-common';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ClockCircleOutlined } from '@ant-design/icons';
import { Typography } from 'antd';

import Permissions from '../auth/Permissions';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import Colors from '../constants/Colors';
import BasicTimer from '../common/clock/BasicTimer';

const { Text } = Typography;

export default function UserRedesignListClock(props) {
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const projects = useSelector((state) => state.projects.projects);

  const projectIdMap = useMemo(() => Common.getIdMap(projects), [projects]);

  const {
    id,
    position,
    tasks,
    timeEntryUserMap,
    onUserClock,
  } = props;
  const formattedPosition = Permissions.formatPosition(position);
  const canClock = (Permissions.id === id && Permissions.has('TIME_TRACKING_SELF'))
  || Permissions.has(`TIME_TRACKING_${formattedPosition}`);

  const formattedDay = useMemo(() => TaskHelpers.formatTodaysTasks(tasks), [timeEntryUserMap, id]);
  const {
    isRunning,
    project,
    costcode,
  } = useMemo(() => {
    const {
      currentTask,
    } = formattedDay;
    if (currentTask) {
      const {
        startTime,
        endTime,
        type = 'work',
      } = currentTask;
      return {
        isRunning: startTime && !endTime && type !== 'break' && type !== 'pto',
        project: projectIdMap[currentTask.projectId]?.name,
        costcode: costcodeIdMap[currentTask.costcodeId]?.name,
      };
    }
    return {
      isRunning: false,
      project: null,
      costcode: null,
    };
  }, [formattedDay, costcodeIdMap, projectIdMap]);

  const handleClockClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onUserClock(props);
  };

  if (!formattedDay?.currentTask) {
    if (!canClock) return null;
    return (
      <OnTraccrButton
        title="Clock In"
        iconLeft
        icon={<ClockCircleOutlined color={Colors.GRAY_800} />}
        style={{
          backgroundColor: 'white',
          border: `1px solid ${Colors.GRAY_400}`,
          color: Colors.GRAY_800,
          fontFamily: 'open-sans-600',
          fontSize: 12,
        }}
        onClick={handleClockClick}
      />
    );
  }

  const backgroundColor = isRunning ? Colors.GREEN_50 : Colors.GRAY_100;
  const clockColor = isRunning ? Colors.GREEN_700 : Colors.GRAY_800;

  return (
    <div className="user-clock-display" onClick={handleClockClick}>
      <div className="user-runtime" style={{ backgroundColor }}>
        <ClockCircleOutlined style={{ color: clockColor, marginBottom: 2, marginRight: 4 }} />
        <BasicTimer
          formattedDay={formattedDay}
          style={{ fontSize: 12, fontFamily: 'open-sans-600', color: clockColor }}
        />
      </div>
      { project && (
        <div className="project-pill">
          <Text style={{ color: Colors.GRAY_800 }}>{project}</Text>
        </div>
      )}
      { costcode && (
        <div className="costcode-pill">
          <Text style={{ color: Colors.GRAY_800 }}>{costcode}</Text>
        </div>
      )}
    </div>
  );
}

UserRedesignListClock.propTypes = {
  id: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  tasks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    projectId: PropTypes.string.isRequired,
    costcodeId: PropTypes.string.isRequired,
  })).isRequired,
  timeEntryUserMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    projectId: PropTypes.string.isRequired,
    costcodeId: PropTypes.string.isRequired,
  }))).isRequired,
  onUserClock: PropTypes.func.isRequired,
};
